import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { useSentryUser } from './lib/sentry';
import ErrorBoundary from './components/molecules/ErrorBoundary';
import { FullPageLoading } from './components/molecules/LoadingStates';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import APIDocumentationPage from './pages/APIDocumentationPage';
import AuditDashboardPage from './pages/AuditDashboardPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: 1,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoading message="Authenticating..." />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/auth" replace />;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const isAdmin = user?.email === 'richierodney434@gmail.com';

  if (isLoading) {
    return <FullPageLoading message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return isAdmin ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoading message="Loading..." />;
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

function App() {
  const { initializeAuth, user, isLoading } = useAuthStore();

  // Set up Sentry user context
  useSentryUser(user);

  useEffect(() => {
    // Initialize auth with timeout protection
    const initAuth = async () => {
      try {
        await initializeAuth();
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        // Ensure loading state is cleared
        useAuthStore.setState({ isLoading: false });
      }
    };

    initAuth();
  }, [initializeAuth]);

  // Show loading screen only for initial auth check
  if (isLoading) {
    return <FullPageLoading message="Initializing..." />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <div className="App min-h-screen">
          <Routes>
            {/* Landing Page - Main entry point */}
            <Route 
              path="/" 
              element={
                <PublicRoute>
                  <LandingPage />
                </PublicRoute>
              } 
            />
            
            {/* Auth Page */}
            <Route 
              path="/auth" 
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              } 
            />

            {/* API Documentation - Public */}
            <Route 
              path="/api-docs" 
              element={<APIDocumentationPage />} 
            />
            
            {/* Dashboard - Protected */}
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Audit Dashboard - Admin Only */}
            <Route
              path="/audit-dashboard"
              element={
                <AdminRoute>
                  <AuditDashboardPage />
                </AdminRoute>
              }
            />
            
            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#333',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                maxWidth: '400px',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          
          {/* React Query DevTools - Only in development */}
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </div>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;